/**
 * Google Drive Service
 * Handles OAuth authentication and file operations with Google Drive API
 */

import { CompleteBackup } from './backupService';
import { backendLogger } from './backendLogger';
import { persistence } from 'services/persistence';
import { toDateString } from 'utils/date';

const DRIVE_BACKUP_FILENAME = 'eir-complete-backup.json';
const DRIVE_BACKUP_FOLDER_NAME = 'EIR Health Backup';
const TOKEN_STORAGE_KEY = 'google-drive-token';
const TOKEN_EXPIRY_KEY = 'google-drive-token-expiry';

// Google Drive API scopes - using drive for full file/folder access
// Note: This scope requires app verification for production use
const SCOPES = 'https://www.googleapis.com/auth/drive';

let gapiInitialized = false;
let tokenClient: any = null;
let initPromise: Promise<void> | null = null; // Track ongoing initialization

export interface GoogleUser {
  email: string;
  name: string;
  picture?: string;
}

/**
 * Initialize Google API client
 */
export async function initGoogleAuth(): Promise<void> {
  // If already initialized and tokenClient exists, return immediately
  if (tokenClient) {
    gapiInitialized = true;
    return;
  }
  
  // If initialization is already in progress, wait for it
  if (initPromise) {
    return initPromise;
  }
  
  // Reset flag if tokenClient is missing (might have been reset or failed previously)
  if (gapiInitialized && !tokenClient) {
    gapiInitialized = false;
  }

  const clientId = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('REACT_APP_GOOGLE_CLIENT_ID is not configured');
  }

  // Create and store the initialization promise
  initPromise = new Promise((resolve, reject) => {
    // Check if scripts are already loaded
    const gapiScript = document.getElementById('gapi-script');
    const gsiScript = document.getElementById('gsi-script');
    
    const initializeTokenClient = () => {
      const gsi = (window as any).google?.accounts?.oauth2;
      if (!gsi) {
        reject(new Error('Google Sign-In library not loaded'));
        return;
      }
      
      try {
        tokenClient = gsi.initTokenClient({
          client_id: clientId,
          scope: SCOPES,
          callback: '', // Will be set during sign-in
        });
        gapiInitialized = true;
        initPromise = null; // Clear the promise
        resolve();
      } catch (error) {
        initPromise = null; // Clear the promise on error
        reject(error);
      }
    };

    const loadGsiScript = () => {
      if (gsiScript) {
        // Script tag exists, wait for it to load
        if ((window as any).google?.accounts?.oauth2) {
          initializeTokenClient();
        } else {
          // Wait for script to load with polling
          let attempts = 0;
          const maxAttempts = 100; // 10 seconds total
          const checkInterval = setInterval(() => {
            attempts++;
            if ((window as any).google?.accounts?.oauth2) {
              clearInterval(checkInterval);
              initializeTokenClient();
            } else if (attempts >= maxAttempts) {
              clearInterval(checkInterval);
              initPromise = null; // Clear the promise on error
              reject(new Error('Google Sign-In script failed to load (timeout)'));
            }
          }, 100);
        }
      } else {
        // Load GSI script
        const script = document.createElement('script');
        script.id = 'gsi-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeTokenClient;
        script.onerror = () => {
          initPromise = null; // Clear the promise on error
          reject(new Error('Failed to load Google Sign-In script'));
        };
        document.body.appendChild(script);
      }
    };

    const initializeGapi = async () => {
      try {
        await (window as any).gapi.client.init({
          discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
        });
        loadGsiScript();
      } catch (error) {
        initPromise = null; // Clear the promise on error
        reject(error);
      }
    };

    if (gapiScript) {
      // GAPI script exists, check if it's loaded
      if ((window as any).gapi?.client) {
        initializeGapi();
      } else {
        // Wait for gapi to load with polling
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds total
        const checkInterval = setInterval(() => {
          attempts++;
          if ((window as any).gapi?.client) {
            clearInterval(checkInterval);
            initializeGapi();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            initPromise = null; // Clear the promise on error
            reject(new Error('Google API script failed to load (timeout)'));
          }
        }, 100);
      }
    } else {
      // Load GAPI script
      const script = document.createElement('script');
      script.id = 'gapi-script';
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        (window as any).gapi.load('client', initializeGapi);
      };
      script.onerror = () => {
        initPromise = null; // Clear the promise on error
        reject(new Error('Failed to load Google API script'));
      };
      document.body.appendChild(script);
    }
  });
}

/**
 * Sign in with Google and request Drive access
 */
export async function signInWithGoogle(): Promise<{ token: string; expiresAt: number }> {
  // Ensure initialization is complete
  await initGoogleAuth();
  
  // Wait for token client to be ready (with timeout)
  let attempts = 0;
  const maxAttempts = 50; // 5 seconds total
  while (!tokenClient && attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
    
    // If still not initialized after a few attempts, try reinitializing
    if (attempts === 10 && !tokenClient) {
      gapiInitialized = false; // Reset flag to allow reinitialization
      await initGoogleAuth();
    }
  }

  // Final check - if still not initialized, throw error
  if (!tokenClient) {
    throw new Error('Token client not initialized. Please refresh the page and try again.');
  }

  return new Promise((resolve, reject) => {
    if (!tokenClient) {
      reject(new Error('Token client not initialized'));
      return;
    }

    tokenClient.callback = (response: any) => {
      if (response.error) {
        reject(new Error(response.error));
        return;
      }

      const token = response.access_token;
      const expiresIn = response.expires_in || 3600;
      const expiresAt = Date.now() + expiresIn * 1000;

      // Store token in sessionStorage (not localStorage for security)
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());

      resolve({ token, expiresAt });
    };

    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
}

/**
 * Sign out from Google
 */
export async function signOutFromGoogle(): Promise<void> {
  // Clear initialization state
  tokenClient = null;
  gapiInitialized = false;
  initPromise = null;
  
  try {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      // Try to revoke the token
      try {
        await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
          method: 'POST',
        });
      } catch (err) {
        console.warn('[GoogleDrive] Failed to revoke token:', err);
        // Continue with sign out even if revoke fails
      }
      
      // Also try the Google accounts API if available
      if ((window as any).google?.accounts?.oauth2?.revoke) {
        try {
          (window as any).google.accounts.oauth2.revoke(token);
        } catch (err) {
          console.warn('[GoogleDrive] Failed to revoke via Google accounts API:', err);
        }
      }
    }
  } catch (error) {
    console.error('[GoogleDrive] Error during sign out:', error);
  } finally {
    // Always clear local storage
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    persistence.removeItem('google-drive-auto-sync');
    persistence.removeItem('google-drive-last-sync');
  }
}

/**
 * Get current access token
 */
export function getAccessToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiryStr) {
    return null;
  }

  const expiry = parseInt(expiryStr, 10);
  if (Date.now() >= expiry) {
    // Token expired
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    return null;
  }

  return token;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAccessToken() !== null;
}

/**
 * Get current Google user info
 */
export async function getCurrentUser(): Promise<GoogleUser | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return {
      email: data.email,
      name: data.name,
      picture: data.picture,
    };
  } catch (error) {
    console.error('[GoogleDrive] Failed to get user info:', error);
    return null;
  }
}

/**
 * Find or create the backup folder in Google Drive
 */
async function findOrCreateBackupFolder(token: string): Promise<string | null> {
  try {
    console.log('[GoogleDrive] Searching for backup folder...');
    // First, try to find the folder
    const searchResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=name='${encodeURIComponent(DRIVE_BACKUP_FOLDER_NAME)}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (searchResponse.ok) {
      const searchData = await searchResponse.json();
      if (searchData.files && searchData.files.length > 0) {
        console.log('[GoogleDrive] Found existing backup folder:', searchData.files[0].id);
        return searchData.files[0].id;
      }
      console.log('[GoogleDrive] Backup folder not found, creating new one...');
    } else {
      const errorData = await searchResponse.json().catch(() => ({}));
      console.warn('[GoogleDrive] Search for folder failed:', {
        status: searchResponse.status,
        statusText: searchResponse.statusText,
        error: errorData,
      });
    }

    // Folder doesn't exist, create it
    console.log('[GoogleDrive] Creating backup folder...');
    const createResponse = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: DRIVE_BACKUP_FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder',
      }),
    });

    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      let errorMessage = `Failed to create backup folder: ${createResponse.status} ${createResponse.statusText}`;
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch {
        errorMessage += ` - ${errorText}`;
      }
      console.error('[GoogleDrive] Folder creation failed:', {
        status: createResponse.status,
        statusText: createResponse.statusText,
        error: errorData,
        errorText,
      });
      throw new Error(errorMessage);
    }

    const folderData = await createResponse.json();
    console.log('[GoogleDrive] Backup folder created successfully:', folderData.id);
    return folderData.id;
  } catch (error: any) {
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      name: error.name,
      error,
    };
    console.error('[GoogleDrive] Failed to find or create backup folder:', errorDetails);
    backendLogger.error('Failed to find or create backup folder', error, {
      function: 'findOrCreateBackupFolder',
      folderName: DRIVE_BACKUP_FOLDER_NAME,
    });
    throw error; // Re-throw to get better error messages
  }
}

/**
 * Find backup file in Google Drive backup folder
 */
async function findBackupFile(token: string, folderId: string): Promise<string | null> {
  try {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=name='${encodeURIComponent(DRIVE_BACKUP_FILENAME)}' and '${folderId}' in parents and trashed=false`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[GoogleDrive] Failed to search for backup file:', response.status, errorText);
      throw new Error(`Failed to search for backup file: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.files && data.files.length > 0) {
      return data.files[0].id;
    }

    return null;
  } catch (error) {
    console.error('[GoogleDrive] Failed to find backup file:', error);
    return null;
  }
}

/**
 * Get the web view link for a file
 */
export async function getFileWebLink(fileId: string): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=webViewLink`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.webViewLink || null;
  } catch (error) {
    console.error('[GoogleDrive] Failed to get file web link:', error);
    return null;
  }
}

/**
 * Upload backup to Google Drive
 */
export async function uploadBackup(backup: CompleteBackup): Promise<void> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated with Google Drive');
  }

  let fileId: string | null = null;
  try {
    // Uploading backup
    
    // Find or create the backup folder
    let folderId: string | null;
    try {
      folderId = await findOrCreateBackupFolder(token);
    } catch (error: any) {
      throw new Error(`Failed to create or find backup folder: ${error.message || error}`);
    }
    
    if (!folderId) {
      throw new Error('Failed to create or find backup folder: Folder ID is null');
    }

    const backupJson = JSON.stringify(backup, null, 2);
    fileId = await findBackupFile(token, folderId);

    const form = new FormData();
    
    if (fileId) {
      // Updating existing file - don't include parents field
      const metadata = {
        name: DRIVE_BACKUP_FILENAME,
        mimeType: 'application/json',
      };
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([backupJson], { type: 'application/json' }));
    } else {
      // Creating new file - include parents field
      const metadata = {
        name: DRIVE_BACKUP_FILENAME,
        mimeType: 'application/json',
        parents: [folderId],
      };
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([backupJson], { type: 'application/json' }));
    }

    const url = fileId
      ? `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=multipart`
      : 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';

    const method = fileId ? 'PATCH' : 'POST';

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: form,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch {
        // Not JSON
      }
      console.error('[GoogleDrive] Upload failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        errorText,
        url,
        method,
      });
      throw new Error(`Failed to upload backup: ${response.status} ${response.statusText} - ${JSON.stringify(errorData || errorText)}`);
    }

    const uploadData = await response.json().catch(() => ({}));
    console.log('[GoogleDrive] Backup uploaded successfully:', {
      fileId: uploadData.id || fileId,
      name: uploadData.name,
    });
  } catch (error: any) {
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      name: error.name,
      error,
    };
    console.error('[GoogleDrive] Upload failed:', errorDetails);
    backendLogger.error('Failed to upload backup to Google Drive', error, {
      function: 'uploadBackup',
      fileName: DRIVE_BACKUP_FILENAME,
      hasFileId: !!fileId,
    });
    throw error;
  }
}

/**
 * Download backup from Google Drive
 */
export async function downloadBackup(): Promise<CompleteBackup | null> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated with Google Drive');
  }

  try {
    // Downloading backup
    
    // Find the backup folder
    const folderId = await findOrCreateBackupFolder(token);
    if (!folderId) {
      // No backup folder found
      return null;
    }

    const fileId = await findBackupFile(token, folderId);

    if (!fileId) {
      // No backup found in Google Drive
      return null;
    }

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download backup: ${response.statusText}`);
    }

    const backup = await response.json();
    // Backup downloaded successfully
    return backup;
  } catch (error) {
    console.error('[GoogleDrive] Download failed:', error);
    throw error;
  }
}

/**
 * Get the web view link for the backup file
 */
export async function getBackupFileLink(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const folderId = await findOrCreateBackupFolder(token);
    if (!folderId) {
      return null;
    }

    const fileId = await findBackupFile(token, folderId);
    if (!fileId) {
      return null;
    }

    return await getFileWebLink(fileId);
  } catch (error) {
    console.error('[GoogleDrive] Failed to get backup file link:', error);
    return null;
  }
}

/**
 * Get the web view link for the backup folder
 */
export async function getBackupFolderLink(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const folderId = await findOrCreateBackupFolder(token);
    if (!folderId) {
      return null;
    }

    return await getFileWebLink(folderId);
  } catch (error) {
    console.error('[GoogleDrive] Failed to get backup folder link:', error);
    return null;
  }
}

/**
 * Delete backup from Google Drive
 */
export async function deleteBackup(): Promise<void> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated with Google Drive');
  }

  try {
    // Deleting backup
    
    const folderId = await findOrCreateBackupFolder(token);
    if (!folderId) {
      // No backup folder found
      return;
    }

    const fileId = await findBackupFile(token, folderId);

    if (!fileId) {
      // No backup found to delete
      return;
    }

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete backup: ${response.statusText}`);
    }

    // Backup deleted successfully
  } catch (error) {
    console.error('[GoogleDrive] Delete failed:', error);
    throw error;
  }
}

/**
 * Get last sync time from cloud backup
 */
export async function getLastSyncTime(): Promise<string | null> {
  try {
    const backup = await downloadBackup();
    return backup?.syncedAt || null;
  } catch (error) {
    console.error('[GoogleDrive] Failed to get last sync time:', error);
    return null;
  }
}

/**
 * Download backup file as JSON (for manual download)
 */
export async function downloadBackupAsFile(): Promise<void> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated with Google Drive');
  }

  try {
    const backup = await downloadBackup();
    if (!backup) {
      throw new Error('No backup found to download');
    }

    // Create a blob and download it
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `eir-backup-${toDateString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('[GoogleDrive] Failed to download backup file:', error);
    throw error;
  }
}

export const googleDriveService = {
  initGoogleAuth,
  signInWithGoogle,
  signOutFromGoogle,
  isAuthenticated,
  getCurrentUser,
  uploadBackup,
  downloadBackup,
  deleteBackup,
  getLastSyncTime,
  getBackupFileLink,
  getBackupFolderLink,
  downloadBackupAsFile,
};
